import * as React from "react"
import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import "../styles/normalize.css"
import styled from 'styled-components'

import Seo from "../components/seo"
import Card from "../components/card"

// images
import strangerThingsImg from "../images/work-stranger-things.jpg"
import avatarImg from "../images/work-avatar.jpg"
import tmntImg from "../images/work-tmnt.jpg"
import hirezImg from "../images/work-hirez.jpg"
import smiteImg from "../images/work-smite.jpg"
import paladinsImg from "../images/work-paladins.jpg"
import rocoImg from "../images/work-roco.jpg"
import toolImg from "../images/work-tool.jpg"
import dropsImg from "../images/work-drops.jpg"

const PageWrapper = styled.main`
  @import url("https://use.typekit.net/ryn0bdm.css");
  --offBlack: #1c2224;

  box-sizing: border-box;
  color: var(--offBlack);
  font-family: interface,ui-sans-serif,sans-serif;
  font-weight: 400;
  font-size: 18px;
  font-style: normal;
  line-height: 28px;
  margin: 0 auto;
  max-width: 1120px;
  padding: 5rem;
  text-decoration-skip-ink: auto;
  text-rendering: optimize legibility;
  width: 100%;
  @media only screen and (max-width: 1120px) {
    padding: 2.5rem;
    max-width: 680px;
  }

  @keyframes wave-animation {
    0% { transform: rotate( 0.0deg) }
    10% { transform: rotate(14.0deg) }
    20% { transform: rotate(-8.0deg) }
    30% { transform: rotate(14.0deg) }
    40% { transform: rotate(-4.0deg) }
    50% { transform: rotate(10.0deg) }
    60% { transform: rotate( 0.0deg) }
    100% { transform: rotate( 0.0deg) }
  }

  h1, h2, h3, h4, h5, h6 {
    color: var(--offBlack);
    font-family: p22-mackinac-pro,ui-serif,serif;
    margin: 0;
    text-rendering: optimize legibility;
  }

  h1 {
    font-size: 44px;
    font-style: normal;
    font-weight: 700;
    line-height: 55px;
    letter-spacing: -1.1px;
  }

  h2 {
    font-size: 26px;
    line-height: 1.5;
    letter-spacing: -0.75px;
    @media only screen and (max-width: 1120px) {
      font-size: 20px;
    }
  }

  a {
    color: var(--offBlack);
    text-decoration: none;
  }

  p {
    margin-bottom: 36px;
  }

  .underline {
    background-image: linear-gradient(120deg, #ef9922 0%, #ef9922 100%);
    background-repeat: no-repeat;
    background-size: 100% 0.2em;
    background-position: 0 88%;
    cursor: pointer;
    transition: background-size 0.25s ease-in;
    &:hover {
      background-size: 100% 100%;
    }
  }

  header {
    align-items: flex-end;
    display: flex;
    h2 {
      font-size: 36px;
      @media only screen and (max-width: 1120px) {
        font-size: 24px;
      }
    }
    nav {
      margin-left: auto;
      a {
        color: var(--offBlack);
        font-size: 24px;
        font-weight: 700;
        opacity: .5;
        text-decoration: underline 1px wavy var(--offBlack);
        text-decoration-skip-ink: none;
        @media only screen and (max-width: 1120px) {
          font-size: 18px;
        }
        &:hover {
          text-decoration: none;
        }
      }
    }
  }

  mark {
    background: none;
    display: block;
    padding: 80px 0;
    @media only screen and (max-width: 1120px) {
      padding: 40px 0;
    }
    .emoji {
      display: inline-block;
      font-size: 50px;
      line-height: 1.5;
      animation-name: wave-animation;
      animation-delay: 1s;
      animation-duration: 2.5s;
      animation-iteration-count: 1;
      transform-origin: 70% 70%;
      @media only screen and (max-width: 1120px) {
        font-size: 36px;
      }
    }
    h1 {
      font-size: 66px;
      line-height: 1.25;
      @media only screen and (max-width: 1120px) {
        font-size: 48px;
      }
    }
    h2 {
      opacity: .4;
      padding-top: 10px;
    }
  }

  aside {
    background: rgba(175, 203, 223, 0.75);
    border-radius: 15px;
    box-sizing: border-box;
    display: flex;
    margin-top: 20px;
    padding: 1.5rem;
    @media only screen and (max-width: 1120px) {
      display: none;
    }
    a {
      &:hover {
        text-decoration: underline 1px wavy var(--offBlack);
      }
      h2 {
        padding-bottom: 5px;
        @media only screen and (max-width: 1120px) {
          font-size: 18px;
        }
      }
    }
    p {
      display: inline;
      font-size: 16px;
      line-height: 1.5;
      margin-bottom: initial;
      @media only screen and (max-width: 1120px) {
        display: block;
        font-size: 14px;
        line-height: 1.5;
        margin: 0;
      }
    }
    .image {
      display: flex;
      padding-right: 30px;
      width: 130px;
      svg {
        height: auto;
        width: 100%;
      }
    }
  }

  section {
    @media only screen and (max-width: 1120px) {
      display: none;
    }
    .cards {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      padding: 22px 0;
      .card {
        margin-bottom: 22px;
      }
    }
    span {
      display: block;
      font-size: 14px;
      font-style: italic;
      line-height: 1.5;
      opacity: .25;
    }
  }
  footer {
    border-top: 2px solid rgba(175, 203, 223, 0.75);
    display: flex;
    margin-top: 5rem;
    @media only screen and (max-width: 1120px) {
      flex-direction: column;
      align-items: center;
      margin-top: initial;
    }
    p {
      margin-bottom: initial;
    }
    .social {
      display: flex;
      align-items: flex-end;
      margin-left: auto;
      @media only screen and (max-width: 1120px) {
        align-items: center;
        margin-left: initial;
        padding-top: 20px;
      }
      a {
        display: block;
        height: 30px;
        opacity: .4;
        transition: ease-in-out .2s opacity;
        width: 30px;
        &:hover {
          opacity: 1;
        }
        &:not(:last-of-type) {
          margin-right: 10px;
        }
        img {
          height: auto;
          width: 100%;
        }
      }
    }
  }
`;

const IndexPage = () => (
  <PageWrapper>

    <Seo title="Brandon Petty" />

    <header>
      <h2>Brandon Petty</h2>
      <nav>
        <Link href="mailto:hello@petty.io">hello?</Link>
      </nav>
    </header>

    <mark>
      <div className="emoji">👋</div>
      <h1>I'm a full stack developer living and working in Atlanta.</h1>
      <h2>Currently writing code as a Senior Software Engineer at <Link href="https://www.hirezstudios.com/" className="underline">Hi-Rez Studios</Link>. Previously <Link href="https://www.amazon.jobs/en/" className="underline">Amazon</Link> and <Link href="https://www.af.mil/" className="underline">United States Air Force</Link>.</h2>
    </mark>
    
    <section>
      <h2>Work</h2>
      <aside>
        <div className="image">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path fill="#78a0cf" d="M13 27A2 2 0 1 0 13 31A2 2 0 1 0 13 27Z"></path><path fill="#f1bc19" d="M77 12A1 1 0 1 0 77 14A1 1 0 1 0 77 12Z"></path><path fill="#cee1f4" d="M50 13A37 37 0 1 0 50 87A37 37 0 1 0 50 13Z"></path><path fill="#f1bc19" d="M83 11A4 4 0 1 0 83 19A4 4 0 1 0 83 11Z"></path><path fill="#78a0cf" d="M87 22A2 2 0 1 0 87 26A2 2 0 1 0 87 22Z"></path><path fill="#fbcd59" d="M81 74A2 2 0 1 0 81 78 2 2 0 1 0 81 74zM15 59A4 4 0 1 0 15 67 4 4 0 1 0 15 59z"></path><path fill="#78a0cf" d="M25 85A2 2 0 1 0 25 89A2 2 0 1 0 25 85Z"></path><path fill="#fff" d="M18.5 51A2.5 2.5 0 1 0 18.5 56A2.5 2.5 0 1 0 18.5 51Z"></path><path fill="#f1bc19" d="M21 66A1 1 0 1 0 21 68A1 1 0 1 0 21 66Z"></path><path fill="#fff" d="M80 33A1 1 0 1 0 80 35A1 1 0 1 0 80 33Z"></path><g><path fill="#ef9922" d="M35,72.3c-4.025,0-7.3-3.274-7.3-7.3V35c0-4.025,3.274-7.3,7.3-7.3h30c4.025,0,7.3,3.274,7.3,7.3 v30c0,4.025-3.274,7.3-7.3,7.3H35z"></path><path fill="#472b29" d="M65,28.4c3.639,0,6.6,2.961,6.6,6.6v30c0,3.639-2.961,6.6-6.6,6.6H35c-3.639,0-6.6-2.961-6.6-6.6 V35c0-3.639,2.961-6.6,6.6-6.6H65 M65,27H35c-4.4,0-8,3.6-8,8v30c0,4.4,3.6,8,8,8h30c4.4,0,8-3.6,8-8V35C73,30.6,69.4,27,65,27 L65,27z"></path></g><g><path fill="#fffef4" d="M68.5,47.875v1.75V64c0,2.475-2.025,4.5-4.5,4.5H36c-2.475,0-4.5-2.025-4.5-4.5V36 c0-2.475,2.025-4.5,4.5-4.5h25.375H64c2.475,0,4.5,2.025,4.5,4.5v3.375v2v1V43.5v2.875V47.875"></path></g><g><path fill="#472b29" d="M68.5,47.375c-0.276,0-0.5-0.224-0.5-0.5V43c0-0.276,0.224-0.5,0.5-0.5S69,42.724,69,43v3.875 C69,47.151,68.776,47.375,68.5,47.375z"></path></g><g><path fill="#472b29" d="M68.5,40.5c-0.276,0-0.5-0.224-0.5-0.5v-2c0-0.276,0.224-0.5,0.5-0.5S69,37.724,69,38v2 C69,40.276,68.776,40.5,68.5,40.5z"></path></g><g><path fill="#472b29" d="M64,69H36c-2.757,0-5-2.243-5-5V36c0-2.757,2.243-5,5-5h25.375c0.276,0,0.5,0.224,0.5,0.5 s-0.224,0.5-0.5,0.5H36c-2.206,0-4,1.794-4,4v28c0,2.206,1.794,4,4,4h28c2.206,0,4-1.794,4-4V49.625c0-0.276,0.224-0.5,0.5-0.5 s0.5,0.224,0.5,0.5V64C69,66.757,66.757,69,64,69z"></path></g><g><path fill="#ef9922" d="M39.544,59.911c-2.137-2.137-2.281-5.469-0.321-7.428l3.184-3.184 c1.959-1.959,5.292-1.816,7.428,0.32c2.137,2.137,2.281,5.469,0.32,7.428l-3.184,3.184C45.013,62.192,41.681,62.048,39.544,59.911z M48.113,51.344c-1.1-1.1-2.816-1.175-3.826-0.165l-3.184,3.184c-1.008,1.008-0.935,2.725,0.165,3.826 c1.1,1.1,2.816,1.175,3.826,0.165l3.184-3.184C49.286,54.159,49.213,52.444,48.113,51.344z"></path><path fill="#472b29" d="M43.558,62.112c-0.085,0-0.169-0.002-0.255-0.005c-1.529-0.066-2.99-0.721-4.112-1.843 c-2.331-2.331-2.476-5.98-0.321-8.136l3.184-3.184c2.155-2.155,5.806-2.01,8.136,0.32c2.331,2.331,2.475,5.981,0.32,8.136 l-3.184,3.184C46.335,61.575,45.006,62.112,43.558,62.112z M45.826,48.418c-1.137,0-2.239,0.407-3.066,1.234l-3.184,3.184 c-1.764,1.765-1.62,4.78,0.321,6.722c0.944,0.944,2.169,1.495,3.448,1.551c1.275,0.059,2.426-0.383,3.272-1.23l3.184-3.184 c1.765-1.765,1.621-4.78-0.32-6.722C48.45,48.941,47.116,48.418,45.826,48.418z M43.333,59.563c-0.858,0-1.739-0.343-2.418-1.021 c-1.295-1.295-1.369-3.328-0.165-4.532l3.184-3.184c0.589-0.588,1.383-0.902,2.254-0.854c0.849,0.036,1.658,0.398,2.278,1.02l0,0 l0,0c1.295,1.295,1.368,3.327,0.165,4.532l-3.184,3.184C44.874,59.279,44.113,59.563,43.333,59.563z M46.047,50.967 c-0.544,0-1.04,0.199-1.406,0.565l-3.184,3.184c-0.814,0.814-0.74,2.213,0.165,3.118c0.906,0.906,2.305,0.98,3.118,0.165 l3.184-3.184c0.813-0.814,0.739-2.213-0.165-3.118l0,0c-0.443-0.443-1.017-0.702-1.614-0.728 C46.112,50.968,46.079,50.967,46.047,50.967z"></path></g><g><path fill="#ef9922" d="M50.073,49.383c-2.137-2.137-2.281-5.469-0.32-7.428l3.184-3.184 c1.959-1.959,5.292-1.816,7.428,0.32c2.137,2.137,2.281,5.469,0.32,7.428l-3.184,3.184C55.542,51.663,52.21,51.519,50.073,49.383z M58.642,40.814c-1.1-1.1-2.816-1.175-3.826-0.165l-3.184,3.184c-1.01,1.01-0.935,2.725,0.165,3.826 c1.1,1.1,2.816,1.175,3.826,0.165l3.184-3.184C59.815,43.63,59.742,41.915,58.642,40.814z"></path><path fill="#472b29" d="M54.087,51.584c-0.084,0-0.169-0.002-0.255-0.006c-1.529-0.065-2.99-0.72-4.112-1.842l0,0 c-1.122-1.122-1.776-2.583-1.843-4.112c-0.066-1.547,0.474-2.975,1.522-4.022l3.184-3.185c2.154-2.153,5.803-2.011,8.136,0.32 c1.122,1.122,1.776,2.583,1.843,4.113c0.066,1.546-0.475,2.975-1.522,4.022l-3.185,3.184 C56.865,51.047,55.535,51.584,54.087,51.584z M50.427,49.029c0.944,0.944,2.17,1.495,3.448,1.55 c1.252,0.063,2.426-0.381,3.272-1.229l3.185-3.184c0.848-0.848,1.284-2.01,1.23-3.272c-0.056-1.279-0.606-2.505-1.551-3.449 c-1.941-1.941-4.958-2.085-6.722-0.32l-3.184,3.185c-0.848,0.848-1.285,2.01-1.23,3.272C48.932,46.859,49.482,48.085,50.427,49.029 L50.427,49.029z M53.861,49.034c-0.858,0-1.739-0.343-2.417-1.021c-1.295-1.295-1.369-3.328-0.166-4.533l3.185-3.184 c1.204-1.204,3.236-1.131,4.532,0.165c1.295,1.295,1.369,3.328,0.165,4.532l-3.184,3.185 C55.403,48.751,54.642,49.034,53.861,49.034z M56.577,40.438c-0.523,0-1.028,0.187-1.407,0.565l-3.185,3.184 c-0.813,0.814-0.739,2.214,0.166,3.119c0.903,0.904,2.304,0.978,3.118,0.165l3.184-3.185c0.814-0.814,0.74-2.213-0.165-3.118 C57.804,40.684,57.179,40.438,56.577,40.438z"></path></g><g><path fill="#ef9922" d="M46.084,53.373c-0.519-0.519-0.557-1.322-0.085-1.794l6.252-6.252 c0.472-0.472,1.274-0.433,1.794,0.085c0.519,0.519,0.557,1.322,0.085,1.794l-6.252,6.252C47.406,53.93,46.602,53.891,46.084,53.373 z"></path><path fill="#472b29" d="M47.059,54.287c-0.473,0-0.956-0.188-1.328-0.561c-0.713-0.713-0.752-1.835-0.086-2.502l6.253-6.252 c0.328-0.329,0.769-0.489,1.257-0.476c0.463,0.022,0.904,0.222,1.244,0.561c0.357,0.357,0.563,0.842,0.563,1.33 c-0.001,0.448-0.17,0.864-0.478,1.171l-6.252,6.253C47.913,54.13,47.49,54.287,47.059,54.287z M53.069,45.495 c-0.182,0-0.346,0.064-0.465,0.185l-6.253,6.252c-0.275,0.276-0.237,0.765,0.086,1.088c0.322,0.323,0.812,0.362,1.087,0.085 l6.252-6.253c0.119-0.118,0.185-0.283,0.185-0.465c0-0.224-0.099-0.451-0.27-0.622c-0.163-0.163-0.371-0.258-0.585-0.269 C53.094,45.496,53.081,45.495,53.069,45.495z"></path></g></svg>
        </div>
        <div className="content">
          <Link href="https://www.gatsbyjs.com/blog/case-study-hi-rez-studios/"><h2>How Hi-Rez Studios Built Their Web Infra On Gatsby & WordPress</h2></Link>
          <p>Millions of monthly users. Eighteen sites. Hi-Rez Studios is a 500-employee game development studio based in Atlanta with a tight-knit team of three developers responsible for Hi-Rez’s entire portfolio of web properties...</p>
        </div>
      </aside>

      <div className="cards">
        <Card 
          image={strangerThingsImg}
          description="AWS, React, GatsbyJS, SASS, Marketing"
          stakeholders={['Netflix']}
          title="SMITE x Stranger Things"
          url="https://www.smitegame.com/"
        />
        <Card 
          image={avatarImg}
          description="AWS, React, GatsbyJS, SASS, Marketing"
          stakeholders={['Nickelodeon']}
          title="SMITE x Avatar: TLA"
          url="https://www.smitegame.com/"
        />
        <Card 
          image={tmntImg}
          description="AWS, React, GatsbyJS, SASS, Marketing"
          stakeholders={['Nickelodeon']}
          title="SMITE x TMNT"
          url="https://www.smitegame.com/"
        />
        <Card 
          image={hirezImg}
          description="AWS, React, GatsbyJS, SASS"
          stakeholders={['Hi-Rez Studios']}
          title="Hi-Rez Studios Rebrands"
          url="https://www.hirezstudios.com/"
        />
        <Card 
          image={dropsImg}
          description="AWS, React, GatsbyJS, SASS"
          stakeholders={['Twitch + Amazon']}
          title="Content Drops"
          url="https://gaming.amazon.com/loot/paladins"
        />
        <Card 
          image={toolImg}
          description="AWS, React, GatsbyJS, SASS"
          stakeholders={['Hi-Rez Studios Localization']}
          title="Centralized i18n Tool"
          url="https://www.hirezstudios.com/"
        />
        <Card 
          image={smiteImg}
          description="AWS, React, GatsbyJS, SASS"
          stakeholders={['Titanforge Games']}
          title="SMITE"
          url="https://www.smitegame.com/"
        />
        <Card 
          image={rocoImg}
          description="AWS, React, GatsbyJS, SASS"
          stakeholders={['First Watch Games']}
          title="Rogue Company"
          url="https://www.roguecompany.com/"
        />
        <Card 
          image={paladinsImg}
          description="AWS, React, GatsbyJS, SASS"
          stakeholders={['Evil Mojo Games']}
          title="Paladins"
          url="https://www.paladins.com/"
        />
      </div>
      <span>Additional work includes OAuth service architecture and development, account linking with Nintendo Online/PlayStation Network/Xbox Live, and leveraging various technologies like JavaScript, React, GatsbyJS, PHP, MongoDB, SQL, SASS, APIs and AWS to build scalable web applications for millions of players around the world.</span>
    </section>
    <footer>
      <p>Handcoded with 🧡 and <a className="underline" href="https://www.gatsbyjs.com/">GatsbyJS</a></p>
      <div className="social">
        <a href="https://twitter.com/brandonpetty">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">    <path d="M28,6.937c-0.957,0.425-1.985,0.711-3.064,0.84c1.102-0.66,1.947-1.705,2.345-2.951c-1.03,0.611-2.172,1.055-3.388,1.295 c-0.973-1.037-2.359-1.685-3.893-1.685c-2.946,0-5.334,2.389-5.334,5.334c0,0.418,0.048,0.826,0.138,1.215 c-4.433-0.222-8.363-2.346-10.995-5.574C3.351,6.199,3.088,7.115,3.088,8.094c0,1.85,0.941,3.483,2.372,4.439 c-0.874-0.028-1.697-0.268-2.416-0.667c0,0.023,0,0.044,0,0.067c0,2.585,1.838,4.741,4.279,5.23 c-0.447,0.122-0.919,0.187-1.406,0.187c-0.343,0-0.678-0.034-1.003-0.095c0.679,2.119,2.649,3.662,4.983,3.705 c-1.825,1.431-4.125,2.284-6.625,2.284c-0.43,0-0.855-0.025-1.273-0.075c2.361,1.513,5.164,2.396,8.177,2.396 c9.812,0,15.176-8.128,15.176-15.177c0-0.231-0.005-0.461-0.015-0.69C26.38,8.945,27.285,8.006,28,6.937z"></path></svg>
        </a>
        <a href="https://www.instagram.com/brandontpetty/">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">    <path d="M 12 3 C 7.04 3 3 7.04 3 12 L 3 38 C 3 42.96 7.04 47 12 47 L 38 47 C 42.96 47 47 42.96 47 38 L 47 12 C 47 7.04 42.96 3 38 3 L 12 3 z M 38 8 L 41 8 C 41.55 8 42 8.45 42 9 L 42 12 C 42 12.55 41.55 13 41 13 L 38 13 C 37.45 13 37 12.55 37 12 L 37 9 C 37 8.45 37.45 8 38 8 z M 25 10 C 30.33 10 35.019688 12.8 37.679688 17 L 42 17 L 42 37 C 42 39.76 39.76 42 37 42 L 13 42 C 10.24 42 8 39.76 8 37 L 8 17 L 12.320312 17 C 14.980313 12.8 19.67 10 25 10 z M 25 12 C 17.83 12 12 17.83 12 25 C 12 32.17 17.83 38 25 38 C 32.17 38 38 32.17 38 25 C 38 17.83 32.17 12 25 12 z M 25 16 C 29.96 16 34 20.04 34 25 C 34 29.96 29.96 34 25 34 C 20.04 34 16 29.96 16 25 C 16 20.04 20.04 16 25 16 z"></path></svg>
        </a>
        <a href="https://github.com/brandonpetty">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">    <path d="M15,3C8.373,3,3,8.373,3,15c0,5.623,3.872,10.328,9.092,11.63C12.036,26.468,12,26.28,12,26.047v-2.051 c-0.487,0-1.303,0-1.508,0c-0.821,0-1.551-0.353-1.905-1.009c-0.393-0.729-0.461-1.844-1.435-2.526 c-0.289-0.227-0.069-0.486,0.264-0.451c0.615,0.174,1.125,0.596,1.605,1.222c0.478,0.627,0.703,0.769,1.596,0.769 c0.433,0,1.081-0.025,1.691-0.121c0.328-0.833,0.895-1.6,1.588-1.962c-3.996-0.411-5.903-2.399-5.903-5.098 c0-1.162,0.495-2.286,1.336-3.233C9.053,10.647,8.706,8.73,9.435,8c1.798,0,2.885,1.166,3.146,1.481C13.477,9.174,14.461,9,15.495,9 c1.036,0,2.024,0.174,2.922,0.483C18.675,9.17,19.763,8,21.565,8c0.732,0.731,0.381,2.656,0.102,3.594 c0.836,0.945,1.328,2.066,1.328,3.226c0,2.697-1.904,4.684-5.894,5.097C18.199,20.49,19,22.1,19,23.313v2.734 c0,0.104-0.023,0.179-0.035,0.268C23.641,24.676,27,20.236,27,15C27,8.373,21.627,3,15,3z"></path></svg>
        </a>
      </div>
    </footer>
  </PageWrapper>
)

export default IndexPage
